/**
 * Feature-local Ukrainian date/time presentation for company-work surfaces.
 * Parses explicit дд.мм.рррр / гг:хх text into canonical local values.
 * Does not use Date(string) guessing. Does not change personal forms.
 */

export const UK_DATE_PLACEHOLDER = "дд.мм.рррр";
export const UK_DATETIME_PLACEHOLDER = "дд.мм.рррр гг:хх";

const DATE_RE = /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/;
const DATETIME_RE = /^(\d{1,2})\.(\d{1,2})\.(\d{4})[ T]+(\d{1,2}):(\d{2})$/;
const LOCAL_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const LOCAL_DATETIME_RE = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/;

function pad2(n) {
  return String(n).padStart(2, "0");
}

export function isLeapYear(year) {
  return year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
}

export function daysInMonth(year, month) {
  if (month === 2) return isLeapYear(year) ? 29 : 28;
  if (month === 4 || month === 6 || month === 9 || month === 11) return 30;
  if (month >= 1 && month <= 12) return 31;
  return 0;
}

export function isValidCivilDate(year, month, day) {
  if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) {
    return false;
  }
  if (year < 1000 || year > 9999) return false;
  if (month < 1 || month > 12) return false;
  if (day < 1 || day > daysInMonth(year, month)) return false;
  return true;
}

export function isValidClockTime(hours, minutes) {
  if (!Number.isInteger(hours) || !Number.isInteger(minutes)) return false;
  if (hours < 0 || hours > 23) return false;
  if (minutes < 0 || minutes > 59) return false;
  return true;
}

function normalizeDraft(text) {
  if (typeof text !== "string") return "";
  return text.replace(/\s+/g, " ").trim();
}

export function formatUkrainianDate(year, month, day) {
  if (!isValidCivilDate(year, month, day)) return "";
  return `${pad2(day)}.${pad2(month)}.${year}`;
}

export function formatUkrainianTime(hours, minutes) {
  if (!isValidClockTime(hours, minutes)) return "";
  return `${pad2(hours)}:${pad2(minutes)}`;
}

export function formatUkrainianDateTime(year, month, day, hours, minutes) {
  const date = formatUkrainianDate(year, month, day);
  const time = formatUkrainianTime(hours, minutes);
  if (!date || !time) return "";
  return `${date} ${time}`;
}

export function localDateStringFromParts(year, month, day) {
  if (!isValidCivilDate(year, month, day)) return "";
  return `${year}-${pad2(month)}-${pad2(day)}`;
}

export function localDateTimeStringFromParts(year, month, day, hours, minutes) {
  const date = localDateStringFromParts(year, month, day);
  if (!date || !isValidClockTime(hours, minutes)) return "";
  return `${date}T${pad2(hours)}:${pad2(minutes)}`;
}

export function isoFromLocalDateTimeString(local) {
  if (!local) return null;
  const match = LOCAL_DATETIME_RE.exec(String(local));
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const hours = Number(match[4]);
  const minutes = Number(match[5]);
  if (!isValidCivilDate(year, month, day) || !isValidClockTime(hours, minutes)) {
    return null;
  }
  return new Date(year, month - 1, day, hours, minutes, 0, 0).toISOString();
}

export function partsFromIso(iso) {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return {
    year: date.getFullYear(),
    month: date.getMonth() + 1,
    day: date.getDate(),
    hours: date.getHours(),
    minutes: date.getMinutes(),
  };
}

export function localDateTimeStringFromIso(iso) {
  const parts = partsFromIso(iso);
  if (!parts) return "";
  return localDateTimeStringFromParts(
    parts.year,
    parts.month,
    parts.day,
    parts.hours,
    parts.minutes,
  );
}

export function ukrainianDateTimeFromIso(iso) {
  const parts = partsFromIso(iso);
  if (!parts) return "";
  return formatUkrainianDateTime(
    parts.year,
    parts.month,
    parts.day,
    parts.hours,
    parts.minutes,
  );
}

export function ukrainianDateFromIso(iso) {
  const parts = partsFromIso(iso);
  if (!parts) return "";
  return formatUkrainianDate(parts.year, parts.month, parts.day);
}

export function ymdFromFilterParam(value) {
  if (!value) return "";
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return "";
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (!isValidCivilDate(year, month, day)) return "";
  return `${match[1]}-${match[2]}-${match[3]}`;
}

export function filterParamFromYmd(ymd, bound) {
  if (!ymd) return "";
  if (!LOCAL_DATE_RE.test(ymd)) return "";
  return bound === "end" ? `${ymd}T23:59:59.999Z` : `${ymd}T00:00:00.000Z`;
}

export function ukrainianFromLocalDateString(ymd) {
  const match = LOCAL_DATE_RE.exec(String(ymd || ""));
  if (!match) return "";
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  return formatUkrainianDate(year, month, day);
}

export function ukrainianFromLocalDateTimeString(local) {
  const match = LOCAL_DATETIME_RE.exec(String(local || ""));
  if (!match) return "";
  return formatUkrainianDateTime(
    Number(match[1]),
    Number(match[2]),
    Number(match[3]),
    Number(match[4]),
    Number(match[5]),
  );
}

export function parseUkrainianDateText(text) {
  const draft = normalizeDraft(text);
  if (!draft) return { status: "empty", canonical: "" };
  const match = DATE_RE.exec(draft);
  if (!match) return { status: "incomplete", canonical: "" };
  const day = Number(match[1]);
  const month = Number(match[2]);
  const year = Number(match[3]);
  if (!isValidCivilDate(year, month, day)) {
    return { status: "invalid", canonical: "" };
  }
  return {
    status: "valid",
    canonical: localDateStringFromParts(year, month, day),
    display: formatUkrainianDate(year, month, day),
  };
}

export function parseUkrainianDateTimeText(text) {
  const draft = normalizeDraft(text);
  if (!draft) return { status: "empty", canonical: "" };
  const match = DATETIME_RE.exec(draft);
  if (!match) return { status: "incomplete", canonical: "" };
  const day = Number(match[1]);
  const month = Number(match[2]);
  const year = Number(match[3]);
  const hours = Number(match[4]);
  const minutes = Number(match[5]);
  if (!isValidCivilDate(year, month, day) || !isValidClockTime(hours, minutes)) {
    return { status: "invalid", canonical: "" };
  }
  return {
    status: "valid",
    canonical: localDateTimeStringFromParts(year, month, day, hours, minutes),
    display: formatUkrainianDateTime(year, month, day, hours, minutes),
  };
}

export function parseUkrainianEditorText(text, mode) {
  return mode === "datetime"
    ? parseUkrainianDateTimeText(text)
    : parseUkrainianDateText(text);
}

export const DATE_PARSE_ERROR = "Вкажіть дату у форматі дд.мм.рррр.";
export const DATETIME_PARSE_ERROR = "Вкажіть дату й час у форматі дд.мм.рррр гг:хх.";

export function parseErrorMessage(mode) {
  return mode === "datetime" ? DATETIME_PARSE_ERROR : DATE_PARSE_ERROR;
}

export function partsFromLocalDateString(ymd) {
  const match = LOCAL_DATE_RE.exec(String(ymd || ""));
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (!isValidCivilDate(year, month, day)) return null;
  return { year, month, day };
}

export function partsFromLocalDateTimeString(local) {
  const match = LOCAL_DATETIME_RE.exec(String(local || ""));
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const hours = Number(match[4]);
  const minutes = Number(match[5]);
  if (!isValidCivilDate(year, month, day) || !isValidClockTime(hours, minutes)) {
    return null;
  }
  return { year, month, day, hours, minutes };
}
